import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  TextField,
  Grid2 as Grid,
  FormControl,
  FormLabel,
} from "@mui/material";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { formControlStyle, formLabelStyle } from "@features/common/styles";
import {
  getSelectedSecret,
  updateSecret,
  SecretUpdateBundle,
  selectSecretsStatus,
} from "./secretsSlice";
import { ISecret, SecretRequest } from "./types";
import { getSelectedCustomerId } from "@features/customers/customerSlice";
import { useAppDispatch } from "@features/common/StoreHook";
import { useState, ChangeEvent, useEffect } from "react";
import EditButtonGroup from "@features/common/ButtonGroups/EditButtonGroup";
import { useCollection } from "@features/common/collectionHook";
import {
  FormDropdownSelect,
  FormTextArea,
  FormTextField,
} from "@features/common/FormFields";
import { permissionsApi } from "@features/authorization/authService";
import { checkPermissions } from "@features/authorization/types";
import { toast } from "react-toastify";
import LoadingIndicatorBox from "@features/common/LoadingIndicatorBox/LoadingIndicatorBox";

/**
 *
 * Editor to update selected secret
 * part of "main" secrets view.
 *
 * TODO: Refresh button because now when updating, we immediately re-fetch values.
 * Sometimes they don't have time to probably update in Azure and thus the old value is shown
 *
 */

const validationSchema = yup.object({
  name: yup.string().required(),
  status: yup.string().nullable(),
  description: yup.string().nullable(),
  additional_info: yup.string().nullable(),
  type: yup.string(),
});

export const SecretEditor = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const customerId = useSelector(getSelectedCustomerId);
  const selectedSecret: ISecret | undefined = useSelector(getSelectedSecret);
  const { data, isLoading: isLoadingPermissions } =
    permissionsApi.useGetCustomerPermissionsQuery(customerId);
  const secretPermissions = checkPermissions("secrets", data);
  const isSubmitting = useSelector(selectSecretsStatus) === "submitting";
  const { handleSubmit, control, reset } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: selectedSecret?.name || "",
      description: selectedSecret?.description || "",
      type: selectedSecret?.type || "",
      additional_info: selectedSecret?.additional_info || "",
    },
  });

  const [secretValue, setSecretValue] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const secretTypes = useCollection("secret-types");

  useEffect(() => {
    reset(selectedSecret);
  }, [selectedSecret]);

  const handleSecretValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSecretValue(event.target.value);
  };

  const handleSave = (data: any) => {
    if (selectedSecret) {
      const payload: SecretRequest = {
        secret_name: data.name,
        secret_desc: data.description,
        secret_type: data.type,
        secret_value: secretValue ? secretValue : "",
        secret_additional_info: data.additional_info,
      };

      const dataBundle: SecretUpdateBundle = {
        customerId: customerId,
        secretName: selectedSecret.name,
        secretResource: selectedSecret.location.resource,
        payload: payload,
      };
      dispatch(updateSecret(dataBundle))
        .unwrap()
        .then(() => {
          setIsEditing(false);
          toast.success("Secret updated successfully");
        })
        .catch((err) => {
          toast.error(err.message);
        });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSecretValue("");
    reset(selectedSecret);
  };

  const handleEdit = () => {
    secretPermissions.update
      ? setIsEditing(true)
      : toast.error("No update permission");
  };

  return (
    <FormControl component="fieldset" sx={formControlStyle}>
      <FormLabel component="legend" sx={formLabelStyle}>
        {t("label-secret")}
      </FormLabel>
      {isLoadingPermissions ? (
        <LoadingIndicatorBox show={isLoadingPermissions} />
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormTextField
              name="name"
              control={control}
              label={t("label-secret-name")}
              readOnly={true}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormDropdownSelect
              name="type"
              control={control}
              label={t("label-secret-type")}
              selectItems={secretTypes}
              disabled={!isEditing || isSubmitting}
            />
          </Grid>
          {selectedSecret?.location?.cloud_platform === "Azure" && (
            <>
              <Grid size={{ xs: 12, sm: 6 }} offset={{ sm: 6 }}>
                <FormTextField
                  name="keyvault_secret_type"
                  control={control}
                  label={t("label-keyvault-secret-type")}
                  readOnly={true}
                />
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormTextArea
              name="description"
              control={control}
              label={t("label-secret-description")}
              readOnly={!isEditing || isSubmitting}
              rows={4}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormTextArea
              name="additional_info"
              control={control}
              label={t("label-additional-information")}
              readOnly={!isEditing || isSubmitting}
              rows={4}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              value={secretValue}
              label={t("label-secret-value")}
              onChange={handleSecretValueChange}
              fullWidth
              slotProps={{ input: { readOnly: !isEditing || isSubmitting } }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <EditButtonGroup
              handleEdit={handleEdit}
              disabled={isSubmitting}
              handleSave={handleSubmit(handleSave)}
              handleCancel={handleCancel}
              isEditing={isEditing}
            />
          </Grid>
        </Grid>
      )}
    </FormControl>
  );
};
